import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import SuccessMessage from '../../components/successMessage';
import ExpensesModal from './expensesModal';
import FinancialResult from '../FinancialResult';
import { useExpensesScreen, expenseStatusConfig } from '../hooks/useExpensesScreen';

export default function ExpensesWeb() {
  const {
    expenses,
    totalAmount,
    totalAmountToBePaid,
    totalAmountPaid,
    isLoading,
    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,
    menuAnchorEl,
    menuSelectedExpense,
    openMenu,
    closeMenu,
    openExpensesModal,
    setOpenExpensesModal,
    selectedExpenseId,
    addExpense,
    editExpense,
    payExpense,
    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,
    saveSuccess,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useExpensesScreen();

  return (
    <Box sx={{ p: 3 }}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ justifySelf: { xs: 'center', md: 'start' } }}>
          <FinancialResult selectedDate={selectedDate} />
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ justifySelf: 'center' }}>
          <IconButton onClick={goToPreviousMonth}>
            <ChevronLeft />
          </IconButton>
          <DatePicker
            label={'mês e ano'}
            views={['month', 'year']}
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
          />
          <IconButton onClick={goToNextMonth}>
            <ChevronRight />
          </IconButton>
        </Stack>

        <Box sx={{ justifySelf: { xs: 'center', md: 'end' } }}>
          <Button data-cy="btn-add-expense" variant="outlined" onClick={addExpense}>
            Adicionar Saída
          </Button>
        </Box>
      </Box>

      <Grid
        container
        sx={{ mt: 2, p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
      >
        <Grid item sx={{ width: '15%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Data
          </Typography>
        </Grid>
        <Grid item sx={{ width: '30%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Descrição
          </Typography>
        </Grid>
        <Grid item sx={{ width: '20%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Categoria
          </Typography>
        </Grid>
        <Grid item sx={{ width: '15%', textAlign: 'right' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Valor
          </Typography>
        </Grid>
        <Grid item sx={{ width: '15%', textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Status
          </Typography>
        </Grid>
        <Grid item sx={{ width: '5%' }}></Grid>
      </Grid>

      {expenses.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
          <Typography>Nenhuma saída encontrada para o período.</Typography>
        </Box>
      )}

      {expenses.map((expense) => (
        <Grid
          container
          key={expense.id}
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            alignItems: 'center',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Grid item sx={{ width: '15%' }}>
            <Typography variant="body2">{expense.dueDate}</Typography>
          </Grid>

          <Grid item sx={{ width: '30%' }}>
            <Typography variant="body2">{expense.description}</Typography>
            {expense.observation && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}
              >
                {expense.observation}
              </Typography>
            )}
          </Grid>

          <Grid item sx={{ width: '20%' }}>
            <Typography variant="body2">{expense.category}</Typography>
          </Grid>

          <Grid item sx={{ width: '15%', textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {expense.amount}
            </Typography>
          </Grid>

          <Grid item sx={{ width: '15%', textAlign: 'center' }}>
            <Chip
              label={expense.status}
              color={expenseStatusConfig[expense.status].color}
              variant={expenseStatusConfig[expense.status].variant}
              size="small"
              sx={{ fontWeight: 'bold', minWidth: '85px' }}
            />
          </Grid>

          <Grid item sx={{ width: '5%', textAlign: 'center' }}>
            <IconButton size="small" onClick={(event) => openMenu(event, expense)}>
              <MoreVertIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={editExpense}>Editar</MenuItem>
        <MenuItem onClick={payExpense} disabled={menuSelectedExpense?.status === 'Pago'}>
          Pagar
        </MenuItem>

        <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      {expenses.length > 0 && (
        <Grid container sx={{ p: 1, mt: 1, justifyContent: 'flex-end' }}>
          <Grid item>
            <Stack direction="column" alignItems="flex-end">
              <Typography variant="caption" color="text.secondary">
                Total: <strong>{totalAmount}</strong>
              </Typography>

              <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                Total Pago: <strong>{totalAmountPaid}</strong>
              </Typography>

              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                Total a Pagar: <strong>{totalAmountToBePaid}</strong>
              </Typography>
            </Stack>
          </Grid>

          <Grid item sx={{ width: '5%' }}></Grid>
        </Grid>
      )}

      {openExpensesModal && (
        <ExpensesModal
          open
          onClose={() => setOpenExpensesModal(false)}
          onSaveSuccess={saveSuccess}
          expenseId={selectedExpenseId}
        />
      )}

      <Dialog open={openDeleteModal} onClose={closeDeleteModal}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir essa saída?
            <br />
            Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="outlined" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
